/**
 * Uygulama kodu ve storage key prefix — VITE_APPLICATION_CODE ile uyumlu.
 * API tarafındaki APPLICATION_CODE ile aynı ürün kodu mantığı.
 */

/** Vite define ile kök `.env` APPLICATION_CODE — küçük harf. */
export function getApplicationCode(): string {
  const raw = import.meta.env.VITE_APPLICATION_CODE;
  return String(raw ?? '').trim().toLowerCase();
}

/** API isteklerinde kullanılacak uygulama kodu (örn. /api/users/me/companies?appCode=). Büyük harf. */
export function getApplicationCodeForApi(): string {
  const raw = import.meta.env.VITE_APPLICATION_CODE;
  return String(raw ?? '').trim().toUpperCase();
}

/** Ürün bazlı localStorage prefix: qubepro_core, qubepro_erp, qubepro_bi vb. */
export function getAppStorageKeyPrefix(): string {
  return `qubepro_${getApplicationCode()}`;
}

export type QubeProUiComponentType =
  | 'table'
  | 'form'
  | 'chart'
  | 'kanban'
  | 'calendar'
  | 'panel'
  | 'toolbar'
  | 'widget';

export interface QubeProUiComponentIdentity {
  app?: string;
  module: string;
  page: string;
  componentType: QubeProUiComponentType;
  component: string;
}

export type QubeProDataTableIdentity =
  Omit<QubeProUiComponentIdentity, 'componentType'> & { component?: string };

function normalizeUiKeySegment(value: string | undefined, fallback: string): string {
  const raw = String(value ?? '').trim();
  const compact = raw
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+(.)/g, (_match, chr: string) => chr.toUpperCase())
    .replace(/^[^a-zA-Z]+/, '')
    .replace(/[^a-zA-Z0-9]/g, '');
  if (!compact) return fallback;
  return compact.charAt(0).toLowerCase() + compact.slice(1);
}

function normalizeUiAppCode(value?: string): string {
  const raw = String(value ?? getApplicationCodeForApi() ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
  return raw || 'APP';
}

/**
 * Kurumsal UI kimliği.
 * Format: `APP.module.page.componentType.component` (örn. `CRM.customer.customers.table.main`).
 * DB/userSettings tarafında raporlanabilir ve ürünler arası çakışmayan sabit anahtardır.
 */
export function buildQubeProUiComponentKey(identity: QubeProUiComponentIdentity): string {
  const app = normalizeUiAppCode(identity.app);
  const module = normalizeUiKeySegment(identity.module, 'module');
  const page = normalizeUiKeySegment(identity.page, 'page');
  const component = normalizeUiKeySegment(identity.component, 'main');
  return `${app}.${module}.${page}.${identity.componentType}.${component}`;
}

/** Seçili firma kimliği için localStorage anahtarı (`ProfileDropdown` ile aynı sözleşme). */
export function getSelectedCompanyStorageKey(): string {
  return `${getAppStorageKeyPrefix()}_selected_company_id`;
}

/**
 * `userSettings.dataTableUi.tables` anahtarı — ürünler arası çakışmayı önlemek için
 * `${APPLICATION_CODE küçük harf}.${mantıksalTabloId}` (örn. `core.companies`, `erp.salesOrders`).
 * Uyduda `APPLICATION_CODE` zaten ERP/BPM vb.; senkron `useDataTableUi` + `DataTable` ile aynı sözleşme.
 *
 * Yeni kurumsal kullanım:
 * `dataTableStorageTableId({ module: 'customer', page: 'customers', component: 'main' })`
 * -> `CRM.customer.customers.table.main`.
 *
 * String kullanım geriye uyumluluk için korunur; mevcut kullanıcı ayarlarını kırmaz.
 */
export function dataTableStorageTableId(tableLogicalId: string | QubeProDataTableIdentity): string {
  if (typeof tableLogicalId === 'object' && tableLogicalId !== null) {
    return buildQubeProUiComponentKey({
      app: tableLogicalId.app,
      module: tableLogicalId.module,
      page: tableLogicalId.page,
      componentType: 'table',
      component: tableLogicalId.component ?? 'main',
    });
  }

  const id = String(tableLogicalId ?? '')
    .trim()
    .replace(/^\.+/, '')
    .replace(/\s+/g, '');
  if (!id) return '';
  const app = getApplicationCode();
  if (!app) return id;
  return `${app}.${id}`;
}

/** Header / giriş ekranı marka metni — `VITE_APP_DISPLAY_NAME` yoksa uygulama kodundan türetilir. */
export function getAppDisplayName(): string {
  const raw = import.meta.env.VITE_APP_DISPLAY_NAME;
  const explicit = String(raw ?? '').trim();
  if (explicit) return explicit;
  const code = getApplicationCode();
  if (!code) return 'QubePro';
  return code
    .split(/[-_]/g)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

/** Platform “tüm uygulamalar” hub URL’i — `VITE_PLATFORM_APPS_URL` ile override. */
export function getPlatformAppsUrl(): string {
  const raw = import.meta.env.VITE_PLATFORM_APPS_URL;
  const s = String(raw ?? '').trim();
  if (s) return s.replace(/\/+$/, '');
  const platform = String(import.meta.env.VITE_BASE_URL || import.meta.env.VITE_PLATFORM_URL || '').trim().replace(/\/+$/, '');
  if (platform) return `${platform}/apps`;
  return typeof window !== 'undefined' ? `${window.location.origin}/apps` : '/apps';
}

/** Ürün dokümantasyon merkezi URL'i — `VITE_DOCS_BASE_URL` ile override. */
export function getDocsBaseUrl(appCode?: string): string {
  const raw = import.meta.env.VITE_DOCS_BASE_URL;
  const s = String(raw ?? '').trim();
  if (s) return s.replace(/\/+$/, '');
  const product = String(appCode || getApplicationCodeForApi() || 'CORE')
    .trim()
    .toLowerCase();
  return `/docs/${product || 'core'}`;
}

/**
 * Global AI assistant API yolu.
 *
 * Browser merkezi AI uygulamasına doğrudan cookie/JWT ile gitmez. Varsayılan
 * aynı-origin `/api/assistant` proxy'dir; ürün API'si server-side service token
 * ile `BASE_URL/ai/api/assistant` hedefine gider. Override gerektiğinde build env
 * üzerinden `VITE_GLOBAL_ASSISTANT_API_BASE_PATH` verilebilir.
 */
export function getGlobalAssistantApiBasePath(): string {
  const env = import.meta.env as unknown as {
    VITE_BASE_PATH?: string;
    VITE_GLOBAL_ASSISTANT_API_BASE_PATH?: string;
    VITE_AI_ASSISTANT_API_BASE_PATH?: string;
  };
  const appCode = getApplicationCodeForApi();
  const basePath = String(env.VITE_BASE_PATH || '').trim().replace(/\/+$/, '');
  const explicit = String(
    env.VITE_GLOBAL_ASSISTANT_API_BASE_PATH || env.VITE_AI_ASSISTANT_API_BASE_PATH || '',
  ).trim();
  if (explicit) {
    const cleaned = explicit.replace(/\/+$/, '');
    if (/^https?:\/\//i.test(cleaned)) {
      return '/api/assistant';
    }
    if (/\/ai\/api\/assistant$/i.test(cleaned) && appCode !== 'AI') return '/api/assistant';
    return cleaned.startsWith('/') ? cleaned : `/${cleaned}`;
  }
  if (appCode === 'AI' && basePath) return `${basePath}/api/assistant`;
  return '/api/assistant';
}
